import { DB, convertPlainUsersToDbFormat } from "./db";
import { encryptData } from "./crypto";

declare global {
  interface Window {
    __exportUsers?: (passphrase: string) => Promise<void>;
    __encryptJsonFile?: (passphrase: string) => Promise<void>;
  }
}

// ファイルダウンロード用のヘルパー関数
function downloadFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ファイルアップロード用のヘルパー関数
function uploadFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    };

    input.click();
  });
}

// エクスポート関数の実装（パスフレーズで暗号化）
async function exportUsersImpl(passphrase: string): Promise<void> {
  if (!passphrase || passphrase.length < 4) {
    console.error("❌ パスフレーズは4文字以上で指定してください");
    return;
  }

  try {
    const jsonData = await DB.exportUsersToJson();
    // パスフレーズで暗号化
    const encryptedData = await encryptData(jsonData, passphrase);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `vanalysisshared_users_encrypted_${timestamp}.json`;

    // 暗号化されたデータをJSON形式で保存
    const exportData = JSON.stringify({
      version: "1.0",
      encrypted: true,
      data: encryptedData,
    });

    downloadFile(exportData, filename, "application/json");
    console.log(
      `✅ ユーザーデータを暗号化してエクスポートしました: ${filename}`,
    );
    console.log(`⚠️ パスフレーズを忘れないでください！`);
  } catch (error) {
    console.error("❌ エクスポートに失敗しました:", error);
  }
}

// 平文JSONファイルを暗号化する関数（手動作成したユーザーリスト用）
async function encryptJsonFileImpl(passphrase: string): Promise<void> {
  if (!passphrase || passphrase.length < 4) {
    console.error("❌ パスフレーズは4文字以上で指定してください");
    return;
  }

  try {
    console.log("📂 平文ユーザーJSONファイルを選択してください...");
    const plainJsonData = await uploadFile();

    // 平文ユーザー配列のパース
    const plainUsers = JSON.parse(plainJsonData) as Array<{
      email: string;
      password: string;
      nickname: string;
      color: string;
      sortKey: string;
    }>;

    // 平文ユーザーをDB形式に変換（email/passwordを暗号化）
    const dbFormatJson = convertPlainUsersToDbFormat(plainUsers);

    // DB形式のJSONをパスフレーズで暗号化
    const encryptedData = await encryptData(dbFormatJson, passphrase);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `vanalysisshared_users_encrypted_${timestamp}.json`;

    // 暗号化されたデータをJSON形式で保存
    const exportData = JSON.stringify({
      version: "1.0",
      encrypted: true,
      data: encryptedData,
    });

    downloadFile(exportData, filename, "application/json");
    console.log(
      `✅ ${plainUsers.length}件のユーザーを暗号化しました: ${filename}`,
    );
    console.log(`⚠️ パスフレーズを忘れないでください！`);
  } catch (error) {
    console.error("❌ 暗号化に失敗しました:", error);
  }
}

// windowオブジェクトにエクスポート（開発者ツール用）
if (typeof window !== "undefined") {
  // ラッパー関数を作成してtoString()をオーバーライド
  const exportUsers = Object.assign(
    (passphrase: string) => exportUsersImpl(passphrase),
    { toString: () => "[Function]" },
  );

  const encryptJsonFile = Object.assign(
    (passphrase: string) => encryptJsonFileImpl(passphrase),
    { toString: () => "[Function]" },
  );

  window.__exportUsers = exportUsers;
  window.__encryptJsonFile = encryptJsonFile;
}
